import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import FemaleIcon from '@mui/icons-material/Face2';
import MaleIcon from '@mui/icons-material/Face';
import PublicIcon from '@material-ui/icons/Public';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import DevicesIcon from '@material-ui/icons/Devices';

const DEFAULT_DESCRIPTION =
  'Profesionales bilingües y tomadores de decisiones. Nativos digitales que valoran la practicidad, se informan por internet y buscan contenido que potencie su crecimiento personal y profesional.';

const AGE_BANDS = [
  { label: '25 – 34', percentage: 20.9, accent: '#42a5f5' },
  { label: '35 – 44', sublabel: 'Núcleo de decisión', percentage: 54, accent: '#1565c0', highlight: true },
  { label: '45 – 54', percentage: 25.1, accent: '#283593' },
];

const REACH_STATS = [
  { value: '92%', label: 'Suramérica', icon: PublicIcon, accent: '#00838f' },
  { value: 'A/B/C', label: 'Segmentos socioeconómicos', icon: TrendingUpIcon, accent: '#6a1b9a' },
  { value: 'Digital-first', label: 'Consumo en streaming y web', icon: DevicesIcon, accent: '#c2185b' },
];

const HOBBIES = [
  {
    label: 'Bienestar y deporte',
    accent: '#2e7d32',
    d: 'M17.09 11h4.86c-.16-1.61-.71-3.11-1.54-4.4-1.73.83-2.99 2.45-3.32 4.4M6.91 11c-.33-1.95-1.59-3.57-3.32-4.4-.83 1.29-1.38 2.79-1.54 4.4zm8.16 0c.32-2.59 1.88-4.79 4.06-6-1.6-1.63-3.74-2.71-6.13-2.95V11zm-6.14 0H11V2.05C8.61 2.29 6.46 3.37 4.87 5c2.18 1.21 3.74 3.41 4.06 6m6.14 2H13v8.95c2.39-.24 4.54-1.32 6.13-2.95-2.18-1.21-3.74-3.41-4.06-6M3.59 17.4c1.72-.83 2.99-2.46 3.32-4.4H2.05c.16 1.61.71 3.11 1.54 4.4m13.5-4.4c.33 1.95 1.59 3.57 3.32 4.4.83-1.29 1.38-2.79 1.54-4.4zm-8.16 0c-.32 2.59-1.88 4.79-4.06 6 1.6 1.63 3.74 2.71 6.13 2.95V13z',
  },
  {
    label: 'Compras y lifestyle',
    accent: '#e65100',
    d: 'M7.5 6v.75H5.513c-.96 0-1.764.724-1.865 1.679l-1.263 12A1.875 1.875 0 0 0 4.25 22.5h15.5a1.875 1.875 0 0 0 1.865-2.071l-1.263-12a1.875 1.875 0 0 0-1.865-1.679H16.5V6a4.5 4.5 0 1 0-9 0ZM12 3a3 3 0 0 0-3 3v.75h6V6a3 3 0 0 0-3-3Zm-3 8.25a3 3 0 1 0 6 0v-.75a.75.75 0 0 1 1.5 0v.75a4.5 4.5 0 1 1-9 0v-.75a.75.75 0 0 1 1.5 0v.75Z',
  },
  {
    label: 'Familia y valores',
    accent: '#c2185b',
    d: 'm11.645 20.91-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z',
  },
  {
    label: 'Entretenimiento',
    accent: '#6a1b9a',
    d: 'M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .658.122Z',
  },
];

const GENDER_SPLIT = [
  { value: 60, label: 'Mujeres', colorKey: 'primary', Icon: FemaleIcon },
  { value: 40, label: 'Hombres', colorKey: 'secondary', Icon: MaleIcon },
];

function SectionCard({ children, accent, sx = {} }) {
  const theme = useTheme();
  const accentColor = accent || theme.palette.primary.main;
  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.25 },
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(accentColor, 0.18)}`,
        boxShadow: `0 8px 24px ${alpha(accentColor, 0.08)}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

SectionCard.propTypes = {
  children: PropTypes.node,
  accent: PropTypes.string,
  sx: PropTypes.object,
};

const Audience = ({ themeMode = 'light', content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const title = content.title || 'Nuestra audiencia';
  const description = content.description || DEFAULT_DESCRIPTION;

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        background: `linear-gradient(165deg, ${alpha(primary, 0.05)} 0%, ${alpha(secondary, 0.09)} 48%, transparent 100%)`,
        px: { xs: 1, md: 2 },
        py: { xs: 3, md: 5 },
        my: { xs: 2, md: 3 },
      }}
    >
      <Box textAlign="center" mb={{ xs: 3, md: 4 }}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
          color="secondary"
          gutterBottom
        >
          Perfil de audiencia
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.02em',
            color: 'primary.main',
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 680, mx: 'auto', lineHeight: 1.7, fontSize: { xs: '0.98rem', md: '1.05rem' } }}
        >
          {description}
        </Typography>
      </Box>

      <Grid container spacing={1.5} justifyContent="center" sx={{ mb: 4, maxWidth: 920, mx: 'auto' }}>
        {REACH_STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <Grid item xs={12} sm={4} key={stat.label}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderRadius: 3,
                  px: 2,
                  py: 1.75,
                  background: `linear-gradient(135deg, ${alpha(stat.accent, 0.12)} 0%, ${alpha(stat.accent, 0.04)} 100%)`,
                  border: `1px solid ${alpha(stat.accent, 0.22)}`,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(stat.accent, 0.16),
                    color: stat.accent,
                    flexShrink: 0,
                  }}
                >
                  <Icon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', lineHeight: 1.1, color: stat.accent }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', lineHeight: 1.3 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Grid
        container
        spacing={2}
        alignItems="flex-start"
        sx={{ maxWidth: 1000, mx: 'auto', mb: 2 }}
      >
        <Grid item xs={12} md={7}>
          <SectionCard accent={primary}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 0.25 }}>Distribución por edad</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5, fontSize: '0.88rem' }}>
              El segmento 35–44 concentra la mayor capacidad de decisión de compra.
            </Typography>
            {AGE_BANDS.map((band, index) => (
              <Box key={band.label} sx={{ mb: index < AGE_BANDS.length - 1 ? 1.5 : 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.75}>
                  <Box>
                    <Typography sx={{ fontWeight: band.highlight ? 800 : 600, fontSize: '0.95rem' }}>
                      {band.label}
                      {band.sublabel && (
                        <Chip
                          label={band.sublabel}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            bgcolor: alpha(band.accent, 0.15),
                            color: band.accent,
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 900, color: band.accent, fontSize: '1.05rem' }}>
                    {band.percentage}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: band.highlight ? 12 : 8,
                    borderRadius: 999,
                    bgcolor: alpha(band.accent, 0.12),
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${band.percentage}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${band.accent} 0%, ${alpha(band.accent, 0.65)} 100%)`,
                      boxShadow: band.highlight ? `0 0 12px ${alpha(band.accent, 0.45)}` : 'none',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard accent={secondary}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.25 }}>Género</Typography>
            <Box
              sx={{
                display: 'flex',
                height: 12,
                borderRadius: 999,
                overflow: 'hidden',
                mb: 1.5,
                boxShadow: `inset 0 0 0 1px ${alpha(primary, 0.08)}`,
              }}
            >
              <Box sx={{ width: '60%', bgcolor: primary }} />
              <Box sx={{ width: '40%', bgcolor: secondary }} />
            </Box>
            <Grid container spacing={1.25}>
              {GENDER_SPLIT.map((item) => {
                const color =
                  item.colorKey === 'primary' ? primary : secondary;
                const { Icon } = item;
                return (
                  <Grid item xs={6} key={item.label}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: alpha(color, 0.08),
                        border: `1px solid ${alpha(color, 0.2)}`,
                      }}
                    >
                      <Icon sx={{ fontSize: 30, color, mb: 0.25 }} />
                      <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', color, lineHeight: 1 }}>
                        {item.value}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard accent="#6a1b9a">
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.25 }}>Intereses</Typography>
            <Grid container spacing={1.25}>
              {HOBBIES.map((hobby) => (
                <Grid item xs={6} sm={3} key={hobby.label}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      p: 1.25,
                      textAlign: 'center',
                      bgcolor: alpha(hobby.accent, 0.08),
                      border: `1px solid ${alpha(hobby.accent, 0.2)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        mx: 'auto',
                        mb: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={hobby.accent} width="100%" height="100%">
                        <path d={hobby.d} />
                      </svg>
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2, color: hobby.accent }}>
                      {hobby.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>

      <Box textAlign="center" sx={{ mt: 1 }}>
        <Chip
          label="Edades 18–54 · Bilingües · Tomadores de decisión"
          sx={{
            fontWeight: 600,
            borderRadius: 999,
            px: 1,
            bgcolor: alpha(primary, 0.08),
            color: primary,
            border: `1px solid ${alpha(primary, 0.2)}`,
          }}
        />
      </Box>
    </Box>
  );
};

Audience.propTypes = {
  themeMode: PropTypes.string,
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

Audience.defaultProps = {
  themeMode: 'light',
  content: {},
};

export default Audience;
